import { Eye, EyeOff, Lock, Unlock, Group, Type, Square, Circle, Minus, ArrowUpRight, Pencil, Spline, Image as ImageIcon } from 'lucide-react';
import { layerName, groupName, layerRows } from './whiteboardOps';

const ICON = { rect: Square, ellipse: Circle, line: Minus, arrow: ArrowUpRight, text: Type, image: ImageIcon };
const iconFor = (o) => (o.type === 'path' ? (o.closed ? Spline : Pencil) : ICON[o.type] || Square);

function Toggle({ on, onClick, OnIcon, OffIcon, label, testid }) {
  const I = on ? OnIcon : OffIcon;
  return (
    <button type="button" aria-label={label} title={label} data-testid={testid}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className="h-8 w-8 sm:h-7 sm:w-7 inline-flex items-center justify-center rounded text-slate-500 hover:bg-slate-200/70 hover:text-slate-900">
      <I className="h-3.5 w-3.5" />
    </button>
  );
}

function Row({ active, depth, Icon, name, hidden, locked, onSelect, onHide, onLock, testid, idPart, bold }) {
  return (
    <div role="button" tabIndex={0} data-testid={testid} onClick={onSelect}
      onKeyDown={(e) => { if (e.key === 'Enter') onSelect(); }}
      className={`flex items-center gap-1.5 pr-1 py-0.5 rounded cursor-pointer text-xs transition-colors ${
        active ? 'bg-blue-50 text-blue-900 ring-1 ring-blue-200' : 'hover:bg-slate-100 text-slate-700'} ${hidden ? 'opacity-50' : ''}`}
      style={{ paddingLeft: 6 + depth * 16 }}>
      <Icon className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
      <span className={`flex-1 truncate ${bold ? 'font-semibold' : ''}`}>{name}</span>
      <Toggle on={!hidden} onClick={onHide} OnIcon={Eye} OffIcon={EyeOff} label={hidden ? 'Show' : 'Hide'} testid={`wb-layer-eye-${idPart}`} />
      <Toggle on={locked} onClick={onLock} OnIcon={Lock} OffIcon={Unlock} label={locked ? 'Unlock' : 'Lock'} testid={`wb-layer-lock-${idPart}`} />
    </div>
  );
}

// Front-to-back list. Tapping a row selects that exact object (or the whole group) even when objects overlap.
export function LayersPanel({ objects, selectedIds, onSelect, onFlag }) {
  const rows = layerRows(objects);
  return (
    <div data-testid="wb-layers-panel" className="bg-white rounded-lg border border-slate-200 p-2 flex flex-col min-h-0">
      <div className="flex items-center justify-between px-1 pb-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500">
        <span>Layers · front to back</span><span data-testid="wb-layers-count">{objects.length}</span>
      </div>
      <div className="overflow-y-auto max-h-64 space-y-0.5 pr-0.5">
        {rows.length === 0 && <p className="text-xs text-slate-400 px-1 py-2">No objects yet.</p>}
        {rows.map((r) => {
          if (r.kind === 'group') {
            const ids = r.members.map((m) => m.id);
            const allHidden = r.members.every((m) => m.hidden), allLocked = r.members.every((m) => m.locked);
            return (
              <Row key={`g-${r.gid}`} testid={`wb-layer-group-${r.gid}`} idPart={`group-${r.gid}`} depth={0} Icon={Group} bold
                name={groupName(objects, r.gid)} hidden={allHidden} locked={allLocked}
                active={ids.length > 0 && ids.every((id) => selectedIds.includes(id))}
                onSelect={() => onSelect(ids)} onHide={() => onFlag(ids, { hidden: !allHidden })} onLock={() => onFlag(ids, { locked: !allLocked })} />
            );
          }
          const o = r.o;
          return (
            <Row key={o.id} testid={`wb-layer-${o.id}`} idPart={o.id} depth={r.depth} Icon={iconFor(o)} name={layerName(o)}
              hidden={!!o.hidden} locked={!!o.locked} active={selectedIds.length === 1 && selectedIds[0] === o.id}
              onSelect={() => onSelect([o.id])} onHide={() => onFlag([o.id], { hidden: !o.hidden })} onLock={() => onFlag([o.id], { locked: !o.locked })} />
          );
        })}
      </div>
    </div>
  );
}
